import { useApolloClient } from "@apollo/client";
import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Chip,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { ReviewDocumentMetadataDialog } from "components/Document/ReviewDocumentMetadataDialog/ReviewDocumentMetadataDialog";
import { DocumentFilePicker, extensionFromName } from "components/Document/shared";
import { OmnisearchAutocomplete } from "components/OmnisearchAutocomplete";
import { DocumentType } from "generated-graphql/graphql";
import { useDocumentAI } from "hooks/useDocumentAI";
import { useUploadFiles } from "hooks/useDocumentUpload";
import { useQueueManifestDocumentExtraction } from "hooks/useQueueManifestDocumentExtraction";
import { useUpsertDocuments } from "hooks/useUpsertDocuments";
import { DialogType, useMinimizedDialogs } from "providers/minimizedDialogs";
import { useCallback, useMemo, useState } from "react";
import { FileRejection } from "react-dropzone";
import { DOCUMENTS } from "routes/Customer/Documents";
import { FACILITY_DOCUMENTS } from "routes/Customer/Facility/Documents";
import { documentTypeToLabel } from "util/constants";
import { prettyPrintDateMed } from "util/dates";

import { PROCESSED_MANIFESTS_QUERY } from "./api";

type StagedDocument = {
  id: string;
  title: string;
};

type ManifestUploadDialogProps = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  facilityId?: string;
  manifestTrackingNumber?: string;
  generatorId?: string;
};

type PickableDocument = {
  id: string;
  title: string;
  fileExtension?: string | null;
  documentType: DocumentType;
  createdAt?: string | null;
};

export function ManifestUploadDialog({
  open,
  onClose,
  tenantId,
  facilityId,
  manifestTrackingNumber,
  generatorId,
}: ManifestUploadDialogProps) {
  const alerts = useAlerts();
  const client = useApolloClient();
  const uploadFiles = useUploadFiles();
  const { upsertDocuments } = useUpsertDocuments();
  const { queueManifestDocumentExtraction } = useQueueManifestDocumentExtraction();
  const { createDialog } = useMinimizedDialogs();
  const hasDocumentAI = useDocumentAI();

  const [stagedDocuments, setStagedDocuments] = useState<StagedDocument[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [dropRejectedFiles, setDropRejectedFiles] = useState<FileRejection[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [mtnOverride, setMtnOverride] = useState(manifestTrackingNumber);
  const [generatorIdOverride, setGeneratorIdOverride] = useState(generatorId);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(true);
  const [reviewDocumentIds, setReviewDocumentIds] = useState<string[] | null>(null);

  const hasOverrides = !!(facilityId || mtnOverride || generatorIdOverride);

  const disabledDocumentIds = useMemo(() => {
    return new Set(stagedDocuments.map((d) => d.id));
  }, [stagedDocuments]);

  const defaultSearch = facilityId
    ? `facilityId:${facilityId} documentType:${DocumentType.RcraManifest}`
    : `tenantId:${tenantId} documentType:${DocumentType.RcraManifest}`;

  // --- Launch analyzing dialog after queuing ---

  const launchAnalyzingDialog = useCallback(
    (jobId: string) => {
      const dialogId = `manifest-analyzing-${jobId}`;
      setUploadDialogOpen(false);
      createDialog({
        id: dialogId,
        dialogType: DialogType.DocumentAIAnalyzing,
        props: {
          open: true,
          jobId,
          preferOriginalTitle: true,
          dialogId,
          onReview: (documentIds: string[]) => {
            if (documentIds.length > 0) {
              setReviewDocumentIds(documentIds);
            } else {
              onClose();
            }
          },
          onComplete: () => {
            client.refetchQueries({ include: [PROCESSED_MANIFESTS_QUERY] });
          },
        },
        onDismiss: () => {
          // Clean up the upload dialog when the analyzing dialog is dismissed
          // without clicking "Review"
          if (!reviewDocumentIds) {
            onClose();
          }
        },
      });
    },
    [createDialog, client, onClose, reviewDocumentIds]
  );

  // --- Existing document staging ---

  const handleSelectExistingDocument = useCallback(
    (doc: PickableDocument) => {
      if (disabledDocumentIds.has(doc.id)) return;
      setStagedDocuments((prev) => [...prev, { id: doc.id, title: doc.title }]);
    },
    [disabledDocumentIds]
  );

  const handleRemoveStagedDocument = useCallback((id: string) => {
    setStagedDocuments((prev) => prev.filter((d) => d.id !== id));
  }, []);

  // --- File management ---

  const handleDrop = useCallback((accepted: File[]) => {
    setFiles((prev) => [...prev, ...accepted]);
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // --- Submit: upload new files + queue all documents (new + staged) ---

  const hasAnythingToSubmit = files.length > 0 || stagedDocuments.length > 0;

  const handleSubmit = useCallback(async () => {
    if (!tenantId || !hasAnythingToSubmit) return;

    setIsUploading(true);
    try {
      // Collect document IDs from staged existing documents
      const allDocumentIds = stagedDocuments.map((d) => d.id);

      // Upload new files and create document records
      if (files.length > 0) {
        const storageLinks = await uploadFiles(
          files.map((file) => ({
            file,
            documentData: { tenantId },
          }))
        );

        const validUploads = storageLinks
          .map((storageLink, i) => ({ storageLink, file: files[i] }))
          .filter(
            (entry): entry is { storageLink: string; file: File } => entry.storageLink != null
          );

        if (validUploads.length < files.length) {
          alerts.error(
            `${files.length - validUploads.length} file(s) failed to upload and were skipped.`
          );
        }

        const documents = validUploads.map(({ storageLink, file }) => ({
          tenantId,
          title: file.name,
          fileExtension: extensionFromName(file.name),
          storageLink,
          documentType: DocumentType.RcraManifest,
          isArchived: false,
        }));

        const created = await upsertDocuments({
          documents,
          currentSubmissionDocument: false,
        });

        allDocumentIds.push(...(created ?? []).map((doc) => doc.id));
      }

      // Queue extraction for all documents in one job
      if (allDocumentIds.length > 0) {
        const jobs = await queueManifestDocumentExtraction({
          tenantId,
          documentIds: allDocumentIds,
          facilityId,
          manifestTrackingNumber: mtnOverride,
          generatorId: generatorIdOverride,
        });

        const jobId = jobs[0]?.job.id;
        if (jobId) {
          launchAnalyzingDialog(jobId);
        }
      }
    } catch (err) {
      alerts.error(
        "Failed to upload and queue manifest extraction.",
        err instanceof Error ? err : undefined
      );
    } finally {
      setIsUploading(false);
    }
  }, [
    tenantId,
    hasAnythingToSubmit,
    files,
    stagedDocuments,
    facilityId,
    mtnOverride,
    generatorIdOverride,
    uploadFiles,
    upsertDocuments,
    queueManifestDocumentExtraction,
    launchAnalyzingDialog,
    alerts,
  ]);

  // --- Dialog lifecycle ---

  const handleClose = () => {
    if (!isUploading) {
      setStagedDocuments([]);
      setFiles([]);
      onClose();
    }
  };

  // --- Omnisearch helpers ---

  const resultFilter = useCallback(
    (item: PickableDocument) => item.fileExtension?.toLowerCase() === "pdf",
    []
  );

  const getOptionDisabled = useCallback(
    (item: PickableDocument) => disabledDocumentIds.has(item.id),
    [disabledDocumentIds]
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, item: PickableDocument) => (
      <ListItem key={item.id} {...props} disablePadding>
        <ListItemText
          primary={item.title}
          secondary={`${documentTypeToLabel(item.documentType)} ${item.createdAt ? `· ${prettyPrintDateMed(item.createdAt)}` : ""}`}
        />
      </ListItem>
    ),
    []
  );

  const omnisearchProps = {
    runInitialSearch: true,
    disabled: isUploading,
    defaultSearch,
    placeholder: "Search RCRA manifest documents...",
    getOptionLabel: (item: PickableDocument) => item.title,
    onSelectionChange: (selected: PickableDocument | null) => {
      if (selected) {
        handleSelectExistingDocument(selected);
      }
    },
    resultFilter,
    getOptionDisabled,
    renderOption,
  };

  if (!open) return null;

  return (
    <>
      <Dialog
        open={uploadDialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        title="Extract Manifest"
        hideCancelButton
        primaryActionButtonProps={{
          hideStartIcon: true,
          saveText: hasAnythingToSubmit ? "Upload & Extract" : "Close",
          onClick: hasAnythingToSubmit ? handleSubmit : handleClose,
          disabled: isUploading,
          loading: isUploading,
          loadingText: "Uploading...",
        }}
      >
        <Stack spacing={2}>
          {hasOverrides && (
            <Alert severity="info" variant="outlined">
              The following overrides will be applied during AI extraction:
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                {facilityId && <Chip label="Facility linked" size="small" variant="outlined" />}
                {mtnOverride && (
                  <Chip
                    label={`MTN: ${mtnOverride}`}
                    size="small"
                    onDelete={() => setMtnOverride(undefined)}
                    deleteIcon={<Close />}
                  />
                )}
                {generatorIdOverride && (
                  <Chip
                    label={`Generator ID: ${generatorIdOverride}`}
                    size="small"
                    onDelete={() => setGeneratorIdOverride(undefined)}
                    deleteIcon={<Close />}
                  />
                )}
              </Stack>
            </Alert>
          )}

          {/* Upload new documents */}
          <DocumentFilePicker
            files={files}
            onDrop={handleDrop}
            onRemove={handleRemoveFile}
            canAddMore={!isUploading}
            multiple
            accept=".pdf"
            disabled={isUploading}
            helperText="Drag and drop PDF manifest files here, or click to browse. We'll extract manifest data automatically."
            dropRejectedFiles={dropRejectedFiles}
            setDropRejectedFiles={setDropRejectedFiles}
          />

          {/* Re-extract existing documents (collapsed) */}
          <Accordion
            disableGutters
            elevation={0}
            slotProps={{ transition: { unmountOnExit: false } }}
            sx={{
              "&:before": { display: "none" },
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body2" color="text.secondary">
                Or select existing documents to re-extract
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                <Typography variant="caption" color="text.secondary">
                  Search for previously uploaded RCRA manifest PDFs to re-extract data from them.
                </Typography>
                {facilityId ? (
                  <OmnisearchAutocomplete
                    {...omnisearchProps}
                    dataQuery={FACILITY_DOCUMENTS}
                    dataQueryVariables={{
                      facilityId,
                      sort: [{ field: "createdAt", sort: "desc" }],
                      page: 0,
                      pageSize: 20,
                    }}
                    getItems={(data) => data.documents.items}
                  />
                ) : (
                  <OmnisearchAutocomplete
                    {...omnisearchProps}
                    dataQuery={DOCUMENTS}
                    getItems={(data) => data.documents.items}
                  />
                )}

                {stagedDocuments.length > 0 && (
                  <Stack spacing={1}>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {stagedDocuments.map((doc) => (
                        <Chip
                          key={doc.id}
                          label={doc.title}
                          onDelete={() => handleRemoveStagedDocument(doc.id)}
                          deleteIcon={<Close />}
                          size="small"
                        />
                      ))}
                    </Stack>
                    <Alert severity="warning" variant="outlined" icon={<WarningAmber />}>
                      If any of these documents were previously extracted, the existing manifest
                      data will be replaced.
                    </Alert>
                  </Stack>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Dialog>

      {reviewDocumentIds && (
        <ReviewDocumentMetadataDialog
          open
          onClose={onClose}
          documentIds={reviewDocumentIds}
          tenantId={tenantId}
          hasDocumentAI={hasDocumentAI}
        />
      )}
    </>
  );
}
